import React, { useEffect, useState } from "react";
import { X, Sparkles, ExternalLink } from "lucide-react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { useLang } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Non-blocking corner card: bottom-left (LTR) / bottom-right (RTL). Dismissible.
export const UpdatePopup = () => {
  const { t, isAr } = useLang();
  const { pathname } = useLocation();
  const [update, setUpdate] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (pathname.startsWith("/admin")) return;
    axios.get(`${API}/updates`).then((r) => {
      const newest = r.data && r.data[0];
      if (!newest) return;
      if (localStorage.getItem("tnsq_seen_update") !== newest.id) {
        setUpdate(newest);
        setTimeout(() => setOpen(true), 1600);
      }
    }).catch(() => {});
  }, [pathname]);

  const close = () => {
    if (update) localStorage.setItem("tnsq_seen_update", update.id);
    setOpen(false);
  };

  const title = update && (isAr ? update.title_ar || update.title_en : update.title_en);
  const summary = update && (isAr ? update.summary_ar || update.summary_en : update.summary_en);

  return (
    <AnimatePresence>
      {open && update && (
        <motion.div
          data-testid="update-popup"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-6 start-6 z-[55] w-[calc(100%-3rem)] max-w-sm rounded-2xl bg-white shadow-lift border border-line overflow-hidden"
        >
          <div className="bg-navy-deep text-white px-5 py-3 flex items-center justify-between">
            <span className="inline-flex items-center gap-2 text-gold text-[0.8rem] font-semibold">
              <Sparkles className="h-4 w-4" /> {t({ en: "New tax update", ar: "تحديث ضريبي جديد" })}
            </span>
            <button onClick={close} data-testid="popup-close" className="text-white/70 hover:text-white"><X className="h-4 w-4" /></button>
          </div>
          <div className="p-5">
            <div className="text-[0.7rem] font-semibold text-brandgreen uppercase tracking-wider mb-1.5">{update.category} · {update.date}</div>
            <h3 className="font-display text-lg text-ink leading-snug">{title}</h3>
            <p className="mt-2 text-muted leading-relaxed text-[0.82rem] line-clamp-3">{summary}</p>
            <div className="mt-4 flex items-center gap-3">
              <a href="/tax-updates" onClick={close} data-testid="popup-view-all"
                className="inline-flex items-center gap-2 h-9 px-4 rounded-full bg-navy text-white text-[0.8rem] font-semibold hover:bg-navy-600 transition-colors">
                {t({ en: "See all updates", ar: "كل التحديثات" })}
              </a>
              {update.show_source && update.source_url && (
                <a href={update.source_url} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-[0.8rem] font-semibold text-brandgreen">
                  <ExternalLink className="h-3.5 w-3.5" /> {update.source_label || t({ en: "Source", ar: "المصدر" })}
                </a>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
